import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  Pagination,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Palette as PaletteIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { productService, categoryService } from '../services';
import VariantManagement from '../components/VariantManagement';

const flattenCategories = (nodes, level = 0) => {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((node) => {
    const current = {
      _id: node._id,
      title: node.title,
      level,
    };
    const children = node.subcategories
      ? flattenCategories(node.subcategories, level + 1)
      : [];
    return [current, ...children];
  });
};

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [variantDialog, setVariantDialog] = useState({ open: false, product: null });
  const [downloadingProductId, setDownloadingProductId] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getHierarchical();
      setCategories(flattenCategories(response));
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to fetch categories');
    }
  };

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: 10,
        sort: sortBy,
        order: sortOrder
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedCategory) {
        params.append('categoryId', selectedCategory);
      }

      const response = await productService.getProducts(params);
      setProducts(response.products);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, searchTerm, selectedCategory, sortBy, sortOrder]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const visibleProductIds = new Set(products.map((product) => product._id));
    setSelectedProductIds((prev) => prev.filter((productId) => visibleProductIds.has(productId)));
  }, [products]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };


  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productService.deleteProduct(productId);
        toast.success('Product deleted successfully');
        fetchProducts();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete product');
      }
    }
  };


  const openCreateDialog = () => {
    navigate('/products/add');
  };

  const handleEditProduct = (product) => {
    navigate('/products/edit', { state: { product } });
  };

  const openVariantDialog = (product) => {
    setVariantDialog({ open: true, product });
  };


  const handleTogglePublish = async (product) => {
    try {
      await productService.togglePublish(product._id, !product.isActive);
      toast.success(`Product ${!product.isActive ? 'published' : 'unpublished'} successfully`);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to toggle publish status');
    }
  };

  const handleDownloadFacebookCsv = async (product) => {
    try {
      setDownloadingProductId(product._id);
      const response = await productService.downloadFacebookCsv(product._id);
      downloadBlobResponse(response, `product-${product._id}.csv`);
    } catch (error) {
      await showBlobError(error, 'Failed to download CSV');
    } finally {
      setDownloadingProductId(null);
    }
  };

  const handleToggleProductSelection = (productId) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleToggleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedProductIds(products.map((product) => product._id));
      return;
    }

    setSelectedProductIds([]);
  };

  const downloadBlobResponse = (response, fallbackFileName) => {
    const blob = new Blob([response.data], { type: response.data?.type || 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const contentDisposition = response.headers['content-disposition'] || '';
    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    const fileName = fileNameMatch?.[1] || fallbackFileName;

    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  };

  const showBlobError = async (error, fallbackMessage) => {
    const errorBlob = error.response?.data;
    if (errorBlob instanceof Blob) {
      try {
        const errorText = await errorBlob.text();
        const parsedError = JSON.parse(errorText);
        toast.error(parsedError.message || fallbackMessage);
        return;
      } catch {
        toast.error(fallbackMessage);
        return;
      }
    }

    toast.error(error.response?.data?.message || fallbackMessage);
  };

  const handleBulkDownloadFacebookCsv = async () => {
    try {
      setBulkDownloadLoading(true);
      const response = await productService.downloadBulkFacebookCsv(selectedProductIds);
      downloadBlobResponse(response, 'selected-products-facebook-catalog.csv');
    } catch (error) {
      await showBlobError(error, 'Failed to download selected CSV');
    } finally {
      setBulkDownloadLoading(false);
    }
  };

  const allProductsSelected = products.length > 0 && selectedProductIds.length === products.length;
  const someProductsSelected =
    selectedProductIds.length > 0 && selectedProductIds.length < products.length;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Product Management</Typography>
        <Box display="flex" gap={2}>
          {selectedProductIds.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleBulkDownloadFacebookCsv}
              disabled={bulkDownloadLoading}
            >
              {bulkDownloadLoading ? 'Downloading CSV...' : 'Download Selected CSV'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Search products"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search by title or description..."
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem
                  key={category._id}
                  value={category._id}
                  sx={{ pl: category.level > 0 ? category.level * 2 : 1 }}
                >
                  {category.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            select
            label="Sort By"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            SelectProps={{ native: true }}
          >
            <option value="title">Title</option>
            <option value="createdAt">Created Date</option>
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            select
            label="Order"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            SelectProps={{ native: true }}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </TextField>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allProductsSelected}
                      indeterminate={someProductsSelected}
                      onChange={handleToggleSelectAll}
                      inputProps={{ 'aria-label': 'Select all products' }}
                    />
                  </TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Trending</TableCell>
                  <TableCell>Publish</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product._id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedProductIds.includes(product._id)}
                        onChange={() => handleToggleProductSelection(product._id)}
                        inputProps={{ 'aria-label': `Select ${product.title}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {product.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {product.categoryId?.title || 'No Category'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.isTrending ? 'Trending' : 'Regular'}
                        color={product.isTrending ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={product.isActive}
                            onChange={() => handleTogglePublish(product)}
                            disabled={!product.defaultVariantId}
                          />
                        }
                        label={product.isActive ? 'Published' : 'Draft'}
                      />
                      {!product.defaultVariantId && (
                        <Typography variant="caption" color="error" display="block">
                          No default variant
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(product.createdAt).toLocaleDateString("en-IN", {
                        timeZone: "Asia/Kolkata"
                      })}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Tooltip title="Download CSV">
                          <span>
                            <IconButton
                              onClick={() => handleDownloadFacebookCsv(product)}
                              color="primary"
                              size="small"
                              disabled={downloadingProductId === product._id}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton
                          onClick={() => handleEditProduct(product)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => openVariantDialog(product)}
                          color="secondary"
                          size="small"
                          title="Manage Variants"
                        >
                          <PaletteIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteProduct(product._id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={(event, page) => setPagination(prev => ({ ...prev, currentPage: page }))}
              color="primary"
            />
          </Box>
        </>
      )}

      {/* Variant Management Dialog */}
      <VariantManagement
        product={variantDialog.product}
        open={variantDialog.open}
        onClose={() => setVariantDialog({ open: false, product: null })}
        onVariantUpdate={() => {
          fetchProducts();
          setVariantDialog({ open: false, product: null });
        }}
      />
    </Box>
  );
};

export default Products;
